import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight, ChevronRight, X, AlertTriangle, HelpCircle, Inbox, RotateCcw,
  LayoutGrid, List, Table as TableIcon, SlidersHorizontal, Check,
} from 'lucide-react';

// Render overlays at document.body so ancestor transforms (e.g. .os-animate) never trap
// position:fixed drawers/modals inside a workspace containing block.
const Portal = ({ children }) => (typeof document !== 'undefined' ? createPortal(children, document.body) : null);

/* ACE OS 2.0 shared UI primitives (UI-R2). One responsive component contract each — no parallel
   desktop/mobile forks. Must render inside a `.os-root` (inherits theme vars). No backend writes,
   no route intelligence, no P0–P7 contract changes. Presentation only. */

// Shared overlay behavior: Escape-to-close + body scroll lock while open.
export function useOverlay(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
}

// ── status law: green=ready, amber=attention, red=risk/blocked, gray=unknown, blue-gray=waiting ──
const STATUS = {
  ready:     { label: 'Ready',     color: 'var(--os-success)', bg: 'rgba(95,168,113,.14)' },
  healthy:   { label: 'Healthy',   color: 'var(--os-success)', bg: 'rgba(95,168,113,.14)' },
  attention: { label: 'Attention', color: 'var(--os-warning)', bg: 'rgba(215,173,85,.14)' },
  risk:      { label: 'At Risk',   color: 'var(--os-danger)',  bg: 'rgba(220,61,75,.14)' },
  blocked:   { label: 'Blocked',   color: 'var(--os-danger)',  bg: 'rgba(220,61,75,.14)' },
  unknown:   { label: 'Unknown',   color: 'var(--os-muted)',   bg: 'rgba(166,166,176,.12)' },
  waiting:   { label: 'Waiting',   color: 'var(--os-info)',    bg: 'rgba(108,143,216,.14)' },
};
export const resolveStatus = (s) => STATUS[s] || STATUS.unknown;

export const StatusBadge = ({ status = 'unknown', label, testid }) => {
  const m = resolveStatus(status);
  return (
    <span className="os-chip inline-flex items-center gap-1" data-testid={testid || `status-${status}`}
          style={{ color: m.color, background: m.bg, border: `1px solid ${m.color}44` }}>
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
      {label || m.label}
    </span>
  );
};

// ── Record Card: generic record (Client/Service/Partner/Vehicle) ──
export const RecordCard = ({ title, subtitle, kind, status, meta = [], onOpen, testid }) => (
  <div className="os-card p-4 cursor-pointer" data-testid={testid || 'record-card'} onClick={onOpen}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {kind && <p className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{kind}</p>}
        <h3 className="font-bold text-base mt-1 truncate" style={{ color: 'var(--os-text)' }}>{title}</h3>
        {subtitle && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--os-muted)' }}>{subtitle}</p>}
      </div>
      {status && <StatusBadge status={status} />}
    </div>
    {meta.length > 0 && (
      <div className="grid grid-cols-2 gap-2 mt-3">
        {meta.map((m, i) => (
          <div key={i}><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{m.label}</p>
            <p className="text-sm truncate" style={{ color: 'var(--os-text)' }}>{m.value}</p></div>
        ))}
      </div>
    )}
    <div className="flex items-center justify-end mt-3 text-xs font-semibold" style={{ color: 'var(--os-red)' }}>
      Open <ChevronRight className="h-3.5 w-3.5" />
    </div>
  </div>
);

// ── Service Health Rail: Booking/Payment/Vehicle/Driver/Client/Fulfillment ──
export const ServiceHealthRail = ({ items = [], testid }) => (
  <div className="flex gap-2 overflow-x-auto os-scroll pb-1" data-testid={testid || 'service-health-rail'}>
    {items.map((it) => {
      const m = resolveStatus(it.status);
      return (
        <div key={it.key} className="shrink-0 rounded-lg px-3 py-2 flex items-center gap-2"
             style={{ background: m.bg, border: `1px solid ${m.color}33`, minHeight: 44 }}
             data-testid={`health-${it.key}`} title={`${it.label}: ${m.label}`}>
          <span className="h-2 w-2 rounded-full" style={{ background: m.color }} />
          <span className="text-xs font-semibold" style={{ color: 'var(--os-text)' }}>{it.label}</span>
        </div>
      );
    })}
  </div>
);

// ── Attention Card (MY ACE queue item) ──
export const AttentionCard = ({ title, reason, status = 'attention', dueLabel, actionLabel = 'Resolve', onAction, testid }) => {
  const m = resolveStatus(status);
  return (
    <div className="os-card p-4" data-testid={testid || 'attention-card'} style={{ borderLeft: `3px solid ${m.color}` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><StatusBadge status={status} />{dueLabel && <span className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{dueLabel}</span>}</div>
          <h3 className="font-bold text-base mt-2" style={{ color: 'var(--os-text)' }}>{title}</h3>
          {reason && <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{reason}</p>}
        </div>
        {onAction && <button className="os-btn-primary shrink-0" style={{ minHeight: 40, padding: '8px 14px' }} onClick={onAction} data-testid="attention-action">{actionLabel}</button>}
      </div>
    </div>
  );
};

// ── Decision Card (Auto Lifestyle decisions) ──
export const DecisionCard = ({ question, note, options = [], onChoose, testid }) => (
  <div className="os-card p-4" data-testid={testid || 'decision-card'}>
    <p className="os-chip" style={{ color: 'var(--os-warning)', border: '1px solid var(--os-warning)55' }}>Decision</p>
    <h3 className="font-bold text-lg mt-2" style={{ color: 'var(--os-text)' }}>{question}</h3>
    {note && <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{note}</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
      {options.map((o, i) => (
        <button key={i} onClick={() => onChoose && onChoose(o)} data-testid={`decision-option-${i}`}
                className="os-card p-3 text-left" style={o.recommended ? { borderColor: 'var(--os-success)' } : {}}>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm" style={{ color: 'var(--os-text)' }}>{o.label}</span>
            {o.recommended && <StatusBadge status="ready" label="Recommended" />}
          </div>
          {o.note && <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{o.note}</p>}
        </button>
      ))}
    </div>
  </div>
);

// ── Metric Card (always carries sample size / window when provided) ──
export const MetricCard = ({ label, value, sample, window, trend, unknown, testid }) => (
  <div className="os-card p-4" data-testid={testid || 'metric-card'}>
    <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--os-muted)' }}>{label}</p>
    <p className="os-display text-3xl mt-1" style={{ color: unknown ? 'var(--os-muted)' : 'var(--os-text)' }}>{unknown ? '—' : (value ?? '—')}</p>
    <div className="flex items-center gap-2 mt-1 text-[11px]" style={{ color: 'var(--os-muted)' }}>
      {sample !== undefined && <span>n={sample}</span>}
      {window && <span>· {window}</span>}
      {trend && <span style={{ color: trend > 0 ? 'var(--os-success)' : 'var(--os-danger)' }}>{trend > 0 ? '▲' : '▼'} {Math.abs(trend)}%</span>}
    </div>
  </div>
);

// ── Context Tabs (responsive, scrollable) ──
export const ContextTabs = ({ tabs = [], active, onChange, testid }) => (
  <div className="flex gap-2 overflow-x-auto os-scroll pb-1" data-testid={testid || 'context-tabs'}>
    {tabs.map((t) => (
      <button key={t.id} onClick={() => onChange && onChange(t.id)} data-testid={`ctx-tab-${t.id}`}
              className={`os-section-tab ${t.id === active ? 'active' : ''}`}>
        {t.label}{t.count !== undefined && <span className="ml-1 opacity-70">({t.count})</span>}
      </button>
    ))}
  </div>
);

// ── Action Bar (sticky primary action; safe-area aware) ──
export const ActionBar = ({ primary, secondary = [], testid }) => (
  <div className="sticky bottom-0 os-surface border-t px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2"
       style={{ borderColor: 'var(--os-border)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
       data-testid={testid || 'action-bar'}>
    {primary && <button className="os-btn-primary w-full sm:w-auto sm:order-2 justify-center" onClick={primary.onClick} data-testid="action-primary">{primary.label}</button>}
    <div className="flex gap-2 overflow-x-auto os-scroll sm:flex-1 sm:order-1 -mx-1 px-1">
      {secondary.map((s, i) => <button key={i} className="os-btn-ghost shrink-0" onClick={s.onClick} data-testid={`action-secondary-${i}`}>{s.label}</button>)}
    </div>
  </div>
);

// ── Detail Drawer / Record Peek (right panel on desktop, bottom sheet on mobile) ──
export const DetailDrawer = ({ open, title, subtitle, tabs, activeTab, onTab, onClose, footer, children, testid }) => {
  useOverlay(open, onClose);
  if (!open) return null;
  return (
    <Portal>
    <div className="fixed inset-0 z-[120]" style={{ background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(4px)' }} onClick={onClose} data-testid={testid || 'detail-drawer'}>
      <div onClick={(e) => e.stopPropagation()}
           className="os-elev absolute inset-x-0 bottom-0 rounded-t-2xl max-h-[92vh] os-sheet-in flex flex-col
                      md:inset-y-0 md:right-0 md:left-auto md:w-[480px] md:max-h-none md:rounded-none md:animate-none">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0" style={{ borderColor: 'var(--os-border)' }}>
          <div className="min-w-0"><h3 className="os-display text-2xl truncate">{title}</h3>{subtitle && <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{subtitle}</p>}</div>
          <button onClick={onClose} className="p-1" style={{ color: 'var(--os-muted)' }} data-testid="drawer-close"><X className="h-5 w-5" /></button>
        </div>
        {tabs && <div className="px-4 pt-3 shrink-0"><ContextTabs tabs={tabs} active={activeTab} onChange={onTab} /></div>}
        <div className="flex-1 overflow-y-auto os-scroll p-5">{children}</div>
        {footer && <div className="shrink-0">{footer}</div>}
      </div>
    </div>
    </Portal>
  );
};

// ── Mobile Filter Drawer (bottom sheet) ──
export const MobileFilterDrawer = ({ open, onClose, onApply, children, testid }) => {
  useOverlay(open, onClose);
  if (!open) return null;
  return (
    <Portal>
    <div className="fixed inset-0 z-[120] flex items-end" style={{ background: 'rgba(0,0,0,.72)' }} onClick={onClose} data-testid={testid || 'filter-drawer'}>
      <div onClick={(e) => e.stopPropagation()} className="os-elev w-full rounded-t-2xl os-sheet-in" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--os-border)' }}>
          <div className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" style={{ color: 'var(--os-red)' }} /><h3 className="os-display text-xl">FILTERS</h3></div>
          <button onClick={onClose} style={{ color: 'var(--os-muted)' }} data-testid="filter-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        <div className="px-5 pb-5"><button className="os-btn-primary w-full justify-center" onClick={onApply} data-testid="filter-apply">Apply filters</button></div>
      </div>
    </div>
    </Portal>
  );
};

// ── View toggle: Cards / Compact / Table ──
export const ViewToggle = ({ view = 'cards', onChange, testid }) => {
  const opts = [{ id: 'cards', icon: LayoutGrid }, { id: 'compact', icon: List }, { id: 'table', icon: TableIcon }];
  return (
    <div className="inline-flex rounded-lg p-0.5" style={{ background: 'var(--os-soft)', border: '1px solid var(--os-border)' }} data-testid={testid || 'view-toggle'}>
      {opts.map((o) => (
        <button key={o.id} onClick={() => onChange && onChange(o.id)} data-testid={`view-${o.id}`}
                className="h-9 w-9 flex items-center justify-center rounded-md"
                style={view === o.id ? { background: 'var(--os-red)', color: '#fff' } : { color: 'var(--os-muted)' }}>
          <o.icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
};

// ── State views ──
export const LoadingState = ({ rows = 3, testid }) => (
  <div className="space-y-3" data-testid={testid || 'loading-state'}>
    {Array.from({ length: rows }).map((_, i) => <div key={i} className="os-skeleton" style={{ height: 68 }} />)}
  </div>
);
const StateShell = ({ icon: Icon, tone, title, body, action, testid }) => (
  <div className="os-card p-10 text-center" data-testid={testid}>
    <Icon className="h-8 w-8 mx-auto mb-3" style={{ color: tone }} />
    <p className="font-bold" style={{ color: 'var(--os-text)' }}>{title}</p>
    {body && <p className="text-sm mt-1" style={{ color: 'var(--os-muted)' }}>{body}</p>}
    {action && <button className="os-btn-ghost mt-4" onClick={action.onClick} data-testid="state-action">{action.label}</button>}
  </div>
);
export const EmptyState = ({ title = 'Nothing here yet', body, action }) => <StateShell icon={Inbox} tone="var(--os-muted)" title={title} body={body} action={action} testid="empty-state" />;
export const UnknownState = ({ title = 'Unknown', body = 'Backend truth is not available. This is not a healthy state.' }) => <StateShell icon={HelpCircle} tone="var(--os-muted)" title={title} body={body} testid="unknown-state" />;
export const ErrorState = ({ title = 'Something went wrong', body, onRetry }) => <StateShell icon={AlertTriangle} tone="var(--os-danger)" title={title} body={body} action={onRetry ? { label: 'Retry', onClick: onRetry } : null} testid="error-state" />;

// ── Confirmation modal (material actions) ──
export const ConfirmModal = ({ open, title, body, confirmLabel = 'Confirm', danger, onConfirm, onClose, testid, confirmTestid }) => {
  useOverlay(open, onClose);
  if (!open) return null;
  return (
    <Portal>
    <div className="fixed inset-0 z-[130] flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(4px)' }} onClick={onClose} data-testid={testid || 'confirm-modal'}>
      <div className="os-elev w-full max-w-sm rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="os-display text-2xl">{title}</h3>
        {body && <p className="text-sm mt-2" style={{ color: 'var(--os-muted)' }}>{body}</p>}
        <div className="flex gap-2 mt-5">
          <button className="os-btn-ghost flex-1 justify-center" onClick={onClose} data-testid="confirm-cancel">Cancel</button>
          <button className="flex-1 justify-center os-btn-primary" style={danger ? { background: 'var(--os-danger)' } : {}} onClick={onConfirm} data-testid={confirmTestid || 'confirm-ok'}>{confirmLabel}</button>
        </div>
      </div>
    </div>
    </Portal>
  );
};

// ── Mobile action sheet ──
export const ActionSheet = ({ open, title, actions = [], onClose, testid }) => {
  useOverlay(open, onClose);
  if (!open) return null;
  return (
    <Portal>
    <div className="fixed inset-0 z-[130] flex items-end" style={{ background: 'rgba(0,0,0,.72)' }} onClick={onClose} data-testid={testid || 'action-sheet'}>
      <div onClick={(e) => e.stopPropagation()} className="os-elev w-full rounded-t-2xl os-sheet-in p-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}>
        {title && <p className="os-display text-lg px-3 py-2">{title}</p>}
        {actions.map((a, i) => (
          <button key={i} onClick={() => { a.onClick && a.onClick(); onClose(); }} data-testid={`sheet-action-${i}`}
                  className="w-full text-left px-4 py-3 rounded-lg flex items-center justify-between"
                  style={{ color: a.danger ? 'var(--os-danger)' : 'var(--os-text)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--os-soft)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <span className="text-sm font-semibold">{a.label}</span>
            {a.icon ? <a.icon className="h-4 w-4" /> : <ChevronRight className="h-4 w-4 opacity-50" />}
          </button>
        ))}
      </div>
    </div>
    </Portal>
  );
};

// ── Compact + Table renderers (used by ViewToggle consumers) ──
export const CompactRow = ({ title, subtitle, status, right, onOpen, testid }) => (
  <button onClick={onOpen} data-testid={testid || 'compact-row'}
          className="os-card w-full text-left px-4 py-3 flex items-center gap-3 mb-2">
    {status && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: resolveStatus(status).color }} />}
    <div className="min-w-0 flex-1"><p className="font-semibold text-sm truncate" style={{ color: 'var(--os-text)' }}>{title}</p>
      {subtitle && <p className="text-xs truncate" style={{ color: 'var(--os-muted)' }}>{subtitle}</p>}</div>
    {right && <span className="text-xs shrink-0" style={{ color: 'var(--os-muted)' }}>{right}</span>}
    <ArrowRight className="h-4 w-4 shrink-0" style={{ color: 'var(--os-muted)' }} />
  </button>
);

export const DataTable = ({ columns = [], rows = [], onRow, testid }) => (
  <div className="os-card overflow-x-auto os-scroll" data-testid={testid || 'data-table'}>
    <table className="w-full text-sm" style={{ minWidth: 560 }}>
      <thead><tr style={{ background: 'var(--os-soft)' }}>
        {columns.map((c) => <th key={c.key} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{c.label}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t cursor-pointer" style={{ borderColor: 'var(--os-border)' }} onClick={() => onRow && onRow(r)} data-testid={`table-row-${i}`}>
            {columns.map((c) => <td key={c.key} className="px-3 py-2.5" style={{ color: 'var(--os-text)' }}>{c.render ? c.render(r) : r[c.key]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


/* Shared native ACE OS message composer (SMS / Email). Presentation only — caller wires the
   canonical send/log handlers. Email never fakes delivery when the sender is unverified. */
export const MessageComposer = ({ open, onClose, to, clientId, emailVerified = false, onSendSMS, onLog, initialText = '', testid }) => {
  useOverlay(open, onClose);
  const [mode, setMode] = useState('sms');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMode('sms'); setText(initialText || ''); } }, [open, initialText]);
  if (!open) return null;
  const canSMS = !!to;
  const run = async (fn) => { setBusy(true); try { await fn(); onClose(); } finally { setBusy(false); } };
  return (
    <Portal>
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center px-0 sm:px-4" style={{ background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(4px)' }} onClick={onClose} data-testid={testid || 'message-composer'}>
      <div className="os-elev w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="os-display text-2xl mb-3">New message</h3>
        <div className="flex gap-2 mb-3">
          <button className={mode === 'sms' ? 'os-btn-primary' : 'os-btn-ghost'} style={{ minHeight: 34 }} onClick={() => setMode('sms')} data-testid="composer-mode-sms">SMS</button>
          <button className={mode === 'email' ? 'os-btn-primary' : 'os-btn-ghost'} style={{ minHeight: 34 }} onClick={() => setMode('email')} data-testid="composer-mode-email">Email</button>
        </div>
        {mode === 'sms' && !canSMS && <p className="text-xs mb-2" style={{ color: 'var(--os-warning)' }}>No client phone on file.</p>}
        {mode === 'email' && !emailVerified && <p className="text-xs mb-2" data-testid="composer-email-unverified" style={{ color: 'var(--os-warning)' }}>Email delivery requires verified sender configuration. You can log this as a note (no email will be sent).</p>}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={mode === 'sms' ? 'Text the client…' : 'Email body…'} data-testid="composer-text"
          className="os-input w-full mb-3" style={{ resize: 'vertical' }} />
        <div className="flex gap-2 justify-end">
          <button className="os-btn-ghost" onClick={onClose} data-testid="composer-cancel">Cancel</button>
          {mode === 'sms'
            ? <button className="os-btn-primary" disabled={busy || !canSMS || !text.trim()} onClick={() => run(() => onSendSMS(text.trim()))} data-testid="composer-send-sms">Send SMS</button>
            : <button className="os-btn-primary" disabled={busy || !text.trim() || !clientId} onClick={() => run(() => onLog('note', text.trim()))} data-testid="composer-log-note">Log as note</button>}
        </div>
      </div>
    </div>
    </Portal>
  );
};

export const DocItem = ({ name, meta, status, statusTone = 'unknown', href, onSend, testid }) => (
  <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'var(--os-border)' }} data-testid={testid}>
    <div className="min-w-0">
      {href ? <a href={href} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-1" style={{ color: 'var(--os-red)' }}>{name}</a>
        : <span className="text-sm" style={{ color: 'var(--os-text)' }}>{name}</span>}
      {meta && <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{meta}</p>}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {status && <StatusBadge status={statusTone} label={status} />}
      {onSend && <button className="os-btn-ghost" style={{ minHeight: 30, padding: '4px 10px' }} onClick={onSend}>Send</button>}
    </div>
  </div>
);
